/**
 * ADMIN PANEL - OPTICA INTERNATIONAL
 * This component manages vouchers, vendors, and financial analysis for Optica International
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, onSnapshot, updateDoc, deleteDoc, doc, query, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../LoginComp/firebase';
import jsPDF from 'jspdf';
import * as XLSX from "xlsx";
import {
  PieChart, Pie, Cell, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid
} from "recharts";
import html2canvas from "html2canvas";
import "./community.css";

// ==================== CONSTANTS ====================
const ADMIN_PASSWORD = "EyeCare@932";
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"];

const EXPENSE_TYPES = [
  "Accesories Payments", "Courier & Postages", "Electricity", "EPF Payments",
  "ETF Payments", "Fittings", "Foods & Meals", "Income Tax Payment", "Loan",
  "Printing & Stationary", "Promotion", "Rent", "Repair & Maintanance",
  "Salaries & Wages", "Suppliers Payment", "Telephone Bill", "Traveling & Transport",
  "Water Bill", "Others"
];

const PAYMENT_METHODS = ["Cash", "Card", "Cheque", "Direct Deposit", "Other"];

// ==================== MAIN COMPONENT ====================
function AdminPanelOptica() {
  // ==================== STATE MANAGEMENT ====================
  
  // Navigation State
  const [activeSubMenu, setActiveSubMenu] = useState('createVoucher');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Voucher State
  const [vouchers, setVouchers] = useState([]);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [nextVoucherNumber, setNextVoucherNumber] = useState('0014');

  // Vendor State
  const [vendors, setVendors] = useState([]);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [vendorSearch, setVendorSearch] = useState("");
  const [showVendorList, setShowVendorList] = useState(false);

  // Filter State
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    year: "",
    month: "",
    paymentMethod: ""
  });

  // Modal State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [pendingDate, setPendingDate] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingVoucher, setPendingVoucher] = useState(null);

  // ==================== FORM STATE ====================
  const [formData, setFormData] = useState({
    name: '',
    branch: 'Maradana',
    description: '',
    amount: '',
    typeOfExpenses: '',
    paymentMethod: 'Cash',
    chequeNumber: '',
    depositNumber: '',
    date: new Date().toISOString().split('T')[0],
    preparedBy: '',
    authorizedBy: ''
  });

  // ==================== VENDOR FORM STATE ====================
  const [vendorData, setVendorData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    address: ""
  });

  // ==================== LIFECYCLE METHODS ====================
  
  /**
   * Fetch next voucher number on component mount
   */
  useEffect(() => {
    const getNextVoucherNumber = async () => {
      try {
        const q = query(
          collection(db, "optica_vouchers"),
          orderBy("voucherNumber", "desc"),
          limit(1)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const lastVoucher = snapshot.docs[0].data();
          const nextNumber = (parseInt(lastVoucher.voucherNumber, 10) + 1)
            .toString()
            .padStart(4, "0");
          setNextVoucherNumber(nextNumber);
        } else {
          setNextVoucherNumber("0014"); // Default first voucher
        }
      } catch (error) {
        console.error("Error getting next voucher number:", error);
        setNextVoucherNumber("0014");
      }
    };

    getNextVoucherNumber();
  }, [vouchers]);

  /**
   * Load vouchers from Firestore with real-time updates
   */
  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'optica_vouchers'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const vouchersData = [];
      querySnapshot.forEach((doc) => {
        vouchersData.push({ id: doc.id, ...doc.data() });
      });
      setVouchers(vouchersData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching vouchers:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Load vendors from Firestore
   */
  useEffect(() => {
    fetchVendors();
  }, []);

  // ==================== VENDOR FUNCTIONS ====================
  
  /**
   * Fetch all vendors from database
   */
  const fetchVendors = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "vendors"));
      const vendorList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setVendors(vendorList);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  /**
   * Save or update vendor
   */
  const saveVendor = async () => {
    if (!vendorData.name) {
      alert("Vendor name is required");
      return;
    }

    try {
      if (editingVendorId) {
        const vendorRef = doc(db, "vendors", editingVendorId);
        await updateDoc(vendorRef, vendorData);
        setEditingVendorId(null);
      } else {
        await addDoc(collection(db, "vendors"), {
          ...vendorData,
          createdAt: new Date()
        });
      }

      // Reset form
      setVendorData({ name: "", company: "", phone: "", email: "", address: "" });
      fetchVendors();
    } catch (error) {
      console.error("Error saving vendor:", error);
    }
  };

  /**
   * Delete vendor
   */
  const deleteVendor = async (id) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      await deleteDoc(doc(db, "vendors", id));
      fetchVendors();
    } catch (error) {
      console.error("Error deleting vendor:", error);
    }
  };

  /**
   * Set vendor for editing
   */
  const editVendor = (vendor) => {
    setVendorData({
      name: vendor.name,
      company: vendor.company,
      phone: vendor.phone,
      email: vendor.email,
      address: vendor.address
    });
    setEditingVendorId(vendor.id);
  };

  // ==================== VOUCHER FUNCTIONS ====================
  
  /**
   * Handle form input changes
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  /**
   * Handle filter changes
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  /**
   * Reset voucher form
   */
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      amount: '',
      typeOfExpenses: '',
      paymentMethod: 'Cash',
      chequeNumber: '',
      depositNumber: '',
      date: new Date().toISOString().split('T')[0],
      preparedBy: '',
      authorizedBy: ''
    });
    setEditingVoucher(null);
    setVendorSearch('');
  };

  /**
   * Submit voucher form (create or update)
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let voucherNumber;

      if (editingVoucher) {
        voucherNumber = editingVoucher.voucherNumber;
      } else {
        voucherNumber = nextVoucherNumber;
      }

      let voucherData = {
        ...formData,
        voucherNumber,
        createdAt: editingVoucher ? editingVoucher.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (editingVoucher) {
        await updateDoc(doc(db, "optica_vouchers", editingVoucher.id), voucherData);
      } else {
        const docRef = await addDoc(collection(db, "optica_vouchers"), voucherData);
        voucherData.id = docRef.id;
        generatePDF(voucherData); // Generate PDF for new voucher
        setNextVoucherNumber((parseInt(voucherNumber, 10) + 1).toString().padStart(4, "0"));
      }

      resetForm();
      setLoading(false);
      setActiveSubMenu("viewVouchers");
      setTimeout(() => setActiveSubMenu("createVoucher"), 500);
    } catch (error) {
      console.error("Error saving voucher:", error);
      setLoading(false);
    }
  };

  /**
   * Handle edit request with password verification
   */
  const handleEditRequest = (voucher) => {
    setPendingAction("edit");
    setPendingVoucher(voucher);
    setShowPasswordModal(true);
  };

  /**
   * Handle delete request with password verification
   */
  const handleDeleteRequest = (voucher) => {
    setPendingAction("delete");
    setPendingVoucher(voucher);
    setShowPasswordModal(true);
  };

  /**
   * Generate PDF for voucher
   */
  const generatePDF = (voucher) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a5'
    });
    
    // Header
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, 210, 25, 'F');
    
    // Try to add logo
    try {
      doc.addImage('/optica_logo.png', 'PNG', 15, 3, 20, 20);
    } catch (e) {
      // Fallback if logo not available
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("OI", 20, 12);
    }
    
    // Company details
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text("Optica International (Pvt) Ltd", 105, 12, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("No. 932, Maradana Road, Colombo 08", 105, 18, { align: 'center' });
    doc.text("Tel: 011 268 2277 | Email: opticalanka@gmail.com", 105, 24, { align: 'center' });

    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.line(15, 45, 195, 45);
    
    // Voucher details
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Name: ${voucher.name || '-'}`, 20, 50);
    doc.text(`Voucher Number: ${voucher.voucherNumber}`, 20, 55);
    doc.text(`Date: ${voucher.date}`, 120, 55);
    doc.text(`Type Of Expenses: ${voucher.typeOfExpenses}`, 120, 62);

    // Payment Method with extra info
    let paymentDetails = `Payment Method: ${voucher.paymentMethod}`;
    if (voucher.paymentMethod === "Cheque" && voucher.chequeNumber) {
      paymentDetails += ` (No: ${voucher.chequeNumber})`;
    }
    if (voucher.paymentMethod === "Direct Deposit" && voucher.depositNumber) {
      paymentDetails += ` (No: ${voucher.depositNumber})`;
    }
    doc.text(paymentDetails, 20, 62);

    // Table header
    doc.setFillColor(240, 240, 240);
    doc.rect(15, 70, 180, 8, 'F');
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Description", 20, 75);
    doc.text("Amount (Rs.)", 160, 75, { align: 'right' });
    
    // Table content
    doc.setFont("helvetica", "normal");
    const maxDescriptionWidth = 120;
    const descriptionLines = doc.splitTextToSize(voucher.description, maxDescriptionWidth);
    let descriptionY = 85;
    const lineHeight = 5;
    
    doc.text(descriptionLines, 20, descriptionY);
    const descriptionHeight = descriptionLines.length * lineHeight;
    const amountY = descriptionLines.length > 1 
      ? descriptionY + (descriptionHeight / 2) - 2
      : descriptionY;
    
    doc.text(parseFloat(voucher.amount).toLocaleString('en-US', { 
      minimumFractionDigits: 2 
    }), 160, amountY, { align: 'right' });
    
    const totalStartY = Math.max(95, descriptionY + descriptionHeight + 5);
    doc.line(15, totalStartY, 195, totalStartY);
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Total Amount", 20, totalStartY + 7);
    doc.text(`Rs. ${parseFloat(voucher.amount).toLocaleString('en-US', { 
      minimumFractionDigits: 2 
    })}`, 160, totalStartY + 7, { align: 'right' });
    
    const signaturesY = totalStartY + 15;
    doc.line(15, signaturesY, 195, signaturesY);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Prepared By", 40, signaturesY + 10);
    doc.text("Received By", 140, signaturesY + 10);
    
    doc.setFont("helvetica", "normal");
    doc.text(voucher.preparedBy || 'N/A', 40, signaturesY + 18);
    doc.text(voucher.authorizedBy || 'N/A', 140, signaturesY + 18);
    
    doc.setDrawColor(100, 100, 100);
    doc.line(30, signaturesY + 22, 80, signaturesY + 22);
    doc.line(130, signaturesY + 22, 180, signaturesY + 22);
    
    const footerY = signaturesY + 30;
    if (footerY < 145) {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text("Thank you for your business!", 105, footerY, { align: 'center' });
    }
    
    doc.save(`voucher_${voucher.voucherNumber}.pdf`);
  };

  /**
   * Download chart as PDF
   */
  const downloadChartAsPDF = (id, filename) => {
    const chartElement = document.getElementById(id);
    if (!chartElement) return;
    
    html2canvas(chartElement).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("landscape", "mm", "a4");
      const imgWidth = 280;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save(filename);
    });
  };

  /**
   * Export vouchers to Excel
   */
  const exportToExcel = () => {
    if (!vouchers || vouchers.length === 0) return;

    const formattedData = vouchers.map(v => ({
      "Date": v.date,
      "Voucher Number": v.voucherNumber,
      "Name": v.name,
      "Branch": v.branch,
      "Type Of Expenses": v.typeOfExpenses,
      "Description": v.description,
      "Amount (Rs.)": v.amount,
      "Payment Method": v.paymentMethod,
      "Cheque Number": v.chequeNumber || '',
      "Deposit Number": v.depositNumber || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vouchers");
    XLSX.writeFile(workbook, "Optica_Vouchers.xlsx");
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    navigate('/');
  };

  // ==================== FILTERED VOUCHERS ====================
  const filteredVouchers = vouchers.filter((v) => {
    const voucherDate = new Date(v.date);
    const voucherYear = voucherDate.getFullYear().toString();
    const voucherMonth = (voucherDate.getMonth() + 1).toString().padStart(2, "0");

    return (
      (!filters.year || filters.year === voucherYear) &&
      (!filters.month || filters.month === voucherMonth) &&
      (!filters.paymentMethod || filters.paymentMethod === v.paymentMethod)
    );
  });

  // ==================== RENDER FUNCTIONS ====================
  
  /**
   * Render Create Voucher section
   */
  const renderCreateVoucher = () => {
const isVendorSelected = formData.name && formData.name.trim() !== "";

return (
    <div className="content-section">
      <h2>{editingVoucher ? 'Edit Voucher' : 'Create Voucher'}</h2>

      {/* Vendor Selection with Search */}
      <div className="input-group">
        <label>Vendor Name *</label>
        <input
          type="text"
          placeholder="Search or enter vendor name..."
          value={vendorSearch || formData.name}
          onChange={(e) => {
            setVendorSearch(e.target.value);
            setFormData({ ...formData, name: e.target.value });
            setShowVendorList(true);
          }}
          onFocus={() => setShowVendorList(true)}
          required
        />

        {showVendorList && vendors.length > 0 && (
          <div className="vendor-dropdown">
            {vendors
              .filter(v => v.name?.toLowerCase().includes(vendorSearch.toLowerCase()))
              .map((vendor) => (
                <div
                  key={vendor.id}
                  className="vendor-option"
                  onClick={() => {
                    setFormData({ ...formData, name: vendor.name });
                    setVendorSearch(vendor.name);
                    setShowVendorList(false);
                  }}
                >
                  {vendor.name} {vendor.company && `- ${vendor.company}`}
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="voucher-number-display">
        Voucher Number: {editingVoucher ? editingVoucher.voucherNumber : nextVoucherNumber}
      </div>

      <form className="form-container" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="input-group">
            <label>Description *</label>
            <input 
              type="text" 
              name="description"
              placeholder="Enter description" 
              value={formData.description}
              onChange={handleInputChange}
              required
            />
          </div>
          
          <div className="input-group">
            <label>Amount (Rs.) *</label>
            <input 
              type="number" 
              name="amount"
              placeholder="Enter amount" 
              value={formData.amount}
              onChange={handleInputChange}
              required
              step="0.01"
              min="0"
            />
          </div>

          <div className="input-group">
            <label>Type Of Expenses *</label>
            <select
              name="typeOfExpenses"
              value={formData.typeOfExpenses}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Select Expense Type --</option>
              {EXPENSE_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="input-group">
            <label>Payment Method *</label>
            <select
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleInputChange}
              required
            >
              {PAYMENT_METHODS.map(method => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
          </div>

          {formData.paymentMethod === "Cheque" && (
            <div className="input-group">
              <label>Cheque Number *</label>
              <input
                type="text"
                name="chequeNumber"
                value={formData.chequeNumber}
                onChange={handleInputChange}
                required
              />
            </div>
          )}

          {formData.paymentMethod === "Direct Deposit" && (
            <div className="input-group">
              <label>Deposit Number *</label>
              <input
                type="text"
                name="depositNumber"
                value={formData.depositNumber}
                onChange={handleInputChange}
                required
              />
            </div>
          )}

          <div className="input-group">
            <label>Date *</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={(e) => {
                const today = new Date().toISOString().split("T")[0];
                if (e.target.value < today) {
                  setPendingAction("date");
                  setPendingDate(e.target.value);
                  setShowPasswordModal(true);
                  return;
                }
                handleInputChange(e);
              }}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="input-group">
            <label>Prepared By *</label>
            <input 
              type="text" 
              name="preparedBy"
              placeholder="Enter name" 
              value={formData.preparedBy}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="input-group">
            <label>Authorized By *</label>
            <input 
              type="text" 
              name="authorizedBy"
              placeholder="Enter name" 
              value={formData.authorizedBy}
              onChange={handleInputChange}
      
            />
          </div>
        </div>

        <div className="form-buttons">


        <button
          type="submit"
          className="submit-btn"
          disabled={loading || !isVendorSelected}
        >
          {loading
            ? "Saving..."
            : editingVoucher
            ? "Update Voucher"
            : "Create Voucher & Download PDF"}
        </button>


          {editingVoucher && (
            <button type="button" className="cancel-btn" onClick={resetForm} disabled={loading}>
              Cancel Edit
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

  /**
   * Render View Vouchers section
   */
  const renderViewVouchers = () => (
    <div className="content-section">
      <h2>View All Vouchers</h2>

      {/* Filter Section */}
      <button className="filter-toggle-btn" onClick={() => setShowFilters(!showFilters)}>
        <i className="fas fa-filter"></i> {showFilters ? "Hide Filters" : "Show Filters"}
      </button>

      {showFilters && (
        <div className="filter-section">
          <select name="year" value={filters.year} onChange={handleFilterChange}>
            <option value="">All Years</option>
            {Array.from({ length: 10 }, (_, i) => {
              const year = new Date().getFullYear() - i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>

          <select name="month" value={filters.month} onChange={handleFilterChange}>
            <option value="">All Months</option>
            {["01","02","03","04","05","06","07","08","09","10","11","12"].map((m,i) => (
              <option key={i} value={m}>
                {new Date(0, i).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </select>

          <select name="paymentMethod" value={filters.paymentMethod} onChange={handleFilterChange}>
            <option value="">All Methods</option>
            {PAYMENT_METHODS.map(method => (
              <option key={method} value={method}>{method}</option>
            ))}
          </select>

          <button className="reset-btn" onClick={() => setFilters({ year: "", month: "", paymentMethod: "" })}>
            <i className="fas fa-undo"></i> Reset Filters
          </button>

          <button className="export-btn" onClick={exportToExcel}>
            <i className="fas fa-file-excel"></i> Export Excel
          </button>
        </div>
      )}

      {loading ? (
        <p>Loading vouchers...</p>
      ) : vouchers.length === 0 ? (
        <p>No vouchers found. Create your first voucher.</p>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Voucher Number</th>
                <th>Description</th>
                <th>Amount (Rs.)</th>
                <th>Date</th>
                <th>Payment Method</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVouchers.map((voucher) => (
                <tr key={voucher.id}>
                  <td>{voucher.name || '-'}</td>
                  <td className="voucher-number">{voucher.voucherNumber}</td>
                  <td>{voucher.description}</td>
                  <td className="amount">
                    {parseFloat(voucher.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td>{voucher.date}</td>
                  <td>{voucher.paymentMethod}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn edit-btn" onClick={() => handleEditRequest(voucher)}>
                        <i className="fas fa-edit"></i> Edit
                      </button>
                      {/* <button className="action-btn delete-btn" onClick={() => handleDeleteRequest(voucher)}>
                        <i className="fas fa-trash"></i> Delete
                      </button> */}
                      <button className="action-btn download-btn" onClick={() => generatePDF(voucher)}>
                        <i className="fas fa-download"></i> PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  /**
   * Render Analysis section
   */
  const renderAnalysis = () => {
    // Group vouchers by month-year and type
    const monthlyTypeData = {};
    vouchers.forEach((v) => {
      const d = new Date(v.date);
      const month = d.toLocaleString("default", { month: "short" });
      const year = d.getFullYear();
      const key = `${month}-${year}`;

      if (!monthlyTypeData[key]) monthlyTypeData[key] = {};
      monthlyTypeData[key][v.typeOfExpenses] =
        (monthlyTypeData[key][v.typeOfExpenses] || 0) + parseFloat(v.amount || 0);
    });

    // Convert to chart format
    const monthlyPieCharts = Object.entries(monthlyTypeData).map(([monthYear, types]) => {
      const pieData = Object.entries(types).map(([name, value]) => ({ name, value }));
      return { monthYear, pieData };
    });

    // Yearly data for line chart
    const yearlyData = {};
    vouchers.forEach((v) => {
      const d = new Date(v.date);
      const year = d.getFullYear();
      yearlyData[year] = (yearlyData[year] || 0) + parseFloat(v.amount || 0);
    });
    const yearlyChartData = Object.entries(yearlyData).map(([y, amt]) => ({ year: y, amount: amt }));

    return (
      <div className="content-section">
        <h2>Analysis Report</h2>

        {/* Monthly Pie Charts */}
        {monthlyPieCharts.map(({ monthYear, pieData }, idx) => (
          <details key={idx} className="month-section" open={idx === monthlyPieCharts.length - 1}>
            <summary className="month-title">{monthYear} Expenses by Type</summary>
            <div id={`pieChart-${monthYear}`} style={{ textAlign: "center", marginBottom: "20px" }}>
              <PieChart width={700} height={400}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={150}
                  fill="#8884d8"
                  label
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
              <button onClick={() => downloadChartAsPDF(`pieChart-${monthYear}`, `${monthYear}-expenses.pdf`)}>
                Download PDF
              </button>
            </div>
          </details>
        ))}

        {/* Yearly Line Chart */}
        <div id="yearlyChart" style={{ textAlign: "center", marginTop: "30px" }}>
          <h3>Yearly Expenses Trend</h3>
          <LineChart width={700} height={300} data={yearlyChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="amount" stroke="#82ca9d" />
          </LineChart>
          <button onClick={() => downloadChartAsPDF("yearlyChart", "yearly-expenses.pdf")}>
            Download PDF
          </button>
        </div>
      </div>
    );
  };

  /**
   * Render Add Vendor section
   */
  const renderAddVendor = () => (
    <div className="vendor-main-container">
      {/* Add Vendor Form */}
      <div className="add-vendor-container">
        <h2>{editingVendorId ? "Edit Vendor" : "Add Vendor"}</h2>
        <div className="vendor-form">
          <input
            type="text"
            placeholder="Vendor Name *"
            value={vendorData.name}
            onChange={(e) => setVendorData({ ...vendorData, name: e.target.value })}
          />
          <input
            type="text"
            placeholder="Company"
            value={vendorData.company}
            onChange={(e) => setVendorData({ ...vendorData, company: e.target.value })}
          />
          <input
            type="text"
            placeholder="Phone"
            value={vendorData.phone}
            onChange={(e) => setVendorData({ ...vendorData, phone: e.target.value })}
          />
          <input
            type="email"
            placeholder="Email"
            value={vendorData.email}
            onChange={(e) => setVendorData({ ...vendorData, email: e.target.value })}
          />
          <textarea
            placeholder="Address"
            value={vendorData.address}
            onChange={(e) => setVendorData({ ...vendorData, address: e.target.value })}
          />
          <button className="save-vendor-btn" onClick={saveVendor}>
            {editingVendorId ? "Update Vendor" : "Save Vendor"}
          </button>
          {editingVendorId && (
            <button className="cancel-btn" onClick={() => {
              setEditingVendorId(null);
              setVendorData({ name: "", company: "", phone: "", email: "", address: "" });
            }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Vendor List */}
      <div className="vendor-list-container">
        <h3>All Vendors</h3>
        <div className="vendor-scroll-list">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="vendor-card">
              <div className="vendor-info">
                <b>{vendor.name}</b>
                {vendor.company && <p><strong>Company:</strong> {vendor.company}</p>}
                {vendor.phone && <p><strong>Phone:</strong> {vendor.phone}</p>}
                {vendor.email && <p><strong>Email:</strong> {vendor.email}</p>}
                {vendor.address && <p><strong>Address:</strong> {vendor.address}</p>}
              </div>
              <div className="vendor-actions">
                <button className="edit-btn" onClick={() => editVendor(vendor)}>
                  Edit
                </button>

                {/* <button className="delete-btn" onClick={() => deleteVendor(vendor.id)}>
                  Delete
                </button> */}

              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /**
   * Render Welcome section
   */
  const renderWelcome = () => (
    <div className="content-section welcome">
      <h2>Welcome to Optica International Admin Panel</h2>
      <p>Select an option from the sidebar to manage vouchers.</p>
    </div>
  );

  /**
   * Main content router
   */
  const renderMainContent = () => {
    switch(activeSubMenu) {
      case 'createVoucher':
        return renderCreateVoucher();
      case 'viewVouchers':
        return renderViewVouchers();
      case 'analysis':
        return renderAnalysis();
      case 'addVendor':
        return renderAddVendor();
      default:
        return renderWelcome();
    }
  };

  // ==================== RENDER COMPONENT ====================
  return (
    <div className="admin-panel-institute">
      <div className="top-nav">
        <div className="nav-buttons">
          <button 
            className={activeSubMenu === 'addVendor' ? 'active' : ''}
            onClick={() => setActiveSubMenu('addVendor')}
          >
            <i className="fas fa-plus-circle"></i> Add Vendor
          </button>

          <button 
            className={activeSubMenu === 'createVoucher' ? 'active' : ''}
            onClick={() => setActiveSubMenu('createVoucher')}
          >
            <i className="fas fa-plus-circle"></i> Create Voucher - Optica
          </button>
          
          <button 
            className={activeSubMenu === 'viewVouchers' ? 'active' : ''}
            onClick={() => setActiveSubMenu('viewVouchers')}
          >
            <i className="fas fa-list"></i> View All Vouchers
          </button>

          <button 
            className={activeSubMenu === 'analysis' ? 'active' : ''}
            onClick={() => setActiveSubMenu('analysis')}
          >
            <i className="fas fa-chart-bar"></i> Analysis Report
          </button>
        </div>
        
        <button className="logout-btn" onClick={handleLogout}>
          <i className="fas fa-sign-out-alt"></i> Logout
        </button>

        {/* Password Modal */}
        {showPasswordModal && (
          <div className="modal-overlay">
            <div className="modal-box">
              <h3>Enter Admin Password</h3>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Password"
                autoFocus
              />
              <div className="modal-actions">
                <button
                  onClick={async () => {
                    if (passwordInput === ADMIN_PASSWORD) {
                      if (pendingAction === "date") {
                        handleInputChange({ target: { name: "date", value: pendingDate } });
                      } else if (pendingAction === "edit" && pendingVoucher) {
                        setEditingVoucher(pendingVoucher);
                        setFormData({
                          name: pendingVoucher.name || '',
                          description: pendingVoucher.description,
                          amount: pendingVoucher.amount,
                          typeOfExpenses: pendingVoucher.typeOfExpenses,
                          paymentMethod: pendingVoucher.paymentMethod,
                          chequeNumber: pendingVoucher.chequeNumber || '',
                          depositNumber: pendingVoucher.depositNumber || '',
                          date: pendingVoucher.date,
                          preparedBy: pendingVoucher.preparedBy,
                          authorizedBy: pendingVoucher.authorizedBy,
                        });
                        setVendorSearch(pendingVoucher.name || '');
                        setActiveSubMenu("createVoucher");
                      } else if (pendingAction === "delete" && pendingVoucher) {
                        if (window.confirm("Are you sure you want to delete this voucher?")) {
                          try {
                            await deleteDoc(doc(db, "optica_vouchers", pendingVoucher.id));
                          } catch (error) {
                            console.error("Error deleting voucher:", error);
                          }
                        }
                      }

                      // Reset modal
                      setShowPasswordModal(false);
                      setPasswordInput("");
                      setPendingAction(null);
                      setPendingVoucher(null);
                      setPendingDate("");
                    } else {
                      alert("Wrong Password!!!");
                    }
                  }}
                >
                  Confirm
                </button>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordInput("");
                    setPendingAction(null);
                    setPendingVoucher(null);
                    setPendingDate("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="main-content">
        {renderMainContent()}
      </div>
    </div>
  );
}

export default AdminPanelOptica;